"use client";

import type { CartItemOutputDto, ItemPriceResultDto } from "@sticker-giant/sg-frontend-common";
import { NumberFormatter } from "@sticker-giant/sg-frontend-common";
import { useLayoutEffect, useRef, useState } from "react";
import { trackRollQuantityReset } from "@/lib/analytics/segment";
import { cn } from "@/lib/utils";
import { useRemoveCartItem } from "@/mutations/useRemoveCartItem";
import { useUpdateItemQuantity } from "@/mutations/useUpdateItemQuantity";
import { useCartItemPricing, useCartItemPricingCustom, useFetchCartItemCustomPricing } from "@/queries/useCartItemPricing";
import { useQuantitiesForItem } from "@/queries/useQuantitiesForItem";
import { useNewCartStore } from "@/stores/useNewCartStore";
import { PackagingMode, resolvePackagingMode } from "@/utils/resolvePackagingMode";
import { getRollDriver, needsRollSelection, type RollDriverSelection } from "@/utils/rollQuantityUtils";
import { CartItemActions } from "./CartItemActions";
import { CartItemDetails } from "./CartItemDetails";
import { CartItemDiscount } from "./CartItemDiscount";
import { CartItemError } from "./CartItemError";
import { CartItemPrice } from "./CartItemPrice";
import { CartItemQuantity } from "./CartItemQuantity";
import { CartItemThumbnail } from "./CartItemThumbnail";
import { getPricingDataForQuantity, lostPackagingSelection, resetPackagingForQuantity, resolveCartPackagingSelection } from "./cartPackagingUtils";
import { MaterialWarning } from "./MaterialWarning";
import { PackagingQuantityWarning } from "./PackagingQuantityWarning";
import { RemoveConfirmation } from "./RemoveConfirmation";
import { ReorderBadge } from "./ReorderBadge";
import { useReconcileRollSelection } from "./useReconcileRollSelection";
interface Props {
  item: CartItemOutputDto;
}
const REMOVE_ERROR_MESSAGE = "We couldn't remove this item. Please try again.";
function focusAtIndex(removedIndex: number) {
  requestAnimationFrame(() => {
    const remaining = document.querySelectorAll<HTMLButtonElement>("[data-cart-remove-button]");
    const next = remaining[removedIndex];
    if (next) {
      next.focus();
      return;
    }
    const previous = remaining[removedIndex - 1];
    if (previous) {
      previous.focus();
      return;
    }
    const closeButton = document.querySelector<HTMLButtonElement>("[data-cart-close-button]");
    closeButton?.focus();
  });
}
export function CartItem({
  item
}: Props) {
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const [customOptionIsSelected, setCustomOptionIsSelected] = useState(false);
  const previousQuantityRef = useRef(item.quantity);
  const quantityChangeIdRef = useRef(0);
  const [showPackagingQuantityWarning, setShowPackagingQuantityWarning] = useState(false);
  const isRollSelectionLost = useNewCartStore(state => state.lostRollSelectionItemIds.includes(item.id));
  const [showCartItemDetails, setShowCartItemDetails] = useState(isRollSelectionLost);
  const markRollSelectionLost = useNewCartStore(state_0 => state_0.markRollSelectionLost);
  const clearRollSelectionLost = useNewCartStore(state_1 => state_1.clearRollSelectionLost);
  const isRollMode = resolvePackagingMode(item.sku.finish) === PackagingMode.Roll;
  const {
    quantities,
    presetQuantities
  } = useQuantitiesForItem(item);
  const updateQuantity = useUpdateItemQuantity();
  const deleteItem = useRemoveCartItem(item);
  const priceQuantities = presetQuantities ?? [];
  const {
    data: pricingData
  } = useCartItemPricing(item, priceQuantities);
  const {
    data: customPricingData
  } = useCartItemPricingCustom(item, priceQuantities);
  const fetchCustomPricing = useFetchCartItemCustomPricing(item);
  const activePricingData = getPricingDataForQuantity(item.quantity, pricingData, customPricingData);
  const packagingSelection = resolveCartPackagingSelection({
    item,
    quantity: item.quantity,
    pricingData: activePricingData
  });
  useReconcileRollSelection({
    item,
    pricingData: activePricingData,
    updateQuantity,
    isDetailsOpen: showCartItemDetails,
    setShowWarning: setShowPackagingQuantityWarning,
    setIsDetailsOpen: setShowCartItemDetails
  });
  if (previousQuantityRef.current !== item.quantity) {
    previousQuantityRef.current = item.quantity;
    setCustomOptionIsSelected(false);
  }
  useLayoutEffect(() => {
    if (updateQuantity.isPending !== isUpdatingQuantity) {
      setIsUpdatingQuantity(updateQuantity.isPending);
    }
  }, [updateQuantity.isPending, isUpdatingQuantity]);
  const hasNonPresetQuantity = !!presetQuantities && !presetQuantities.includes(item.quantity);
  const isSingleQuantity = !!quantities && quantities.quantityOptions.length <= 1;
  function handleQuantityChange(quantity: number, isCustom = false) {
    // Any newer quantity change makes an in-flight pricing fetch stale
    const changeId = ++quantityChangeIdRef.current;
    setCustomOptionIsSelected(isCustom);
    const newPricingData = getPricingDataForQuantity(quantity, pricingData, customPricingData);
    // A roll driver can only be carried over against the new quantity's options, so fetch them if uncached
    const rollDriver = getRollDriver(item);
    const preservesRollValue = rollDriver === "ROLL_COUNT" || rollDriver === "ROLL_QTY";
    if (isRollMode && preservesRollValue && !newPricingData) {
      fetchCustomPricing(quantity).catch(() => undefined).then(fetched => {
        if (changeId === quantityChangeIdRef.current) applyQuantityChange(quantity, fetched);
      });
      return;
    }
    applyQuantityChange(quantity, newPricingData);
  }
  function applyQuantityChange(quantity_0: number, newPricingData_0?: ItemPriceResultDto) {
    const initialCartDetailsState = showCartItemDetails;
    const packaging = resetPackagingForQuantity({
      item,
      quantity: quantity_0,
      pricingData: newPricingData_0
    });
    const lostSelection = lostPackagingSelection(item, packaging);
    const lostRollSelection = isRollMode && lostSelection;
    if (lostRollSelection) markRollSelectionLost(item.id);
    if (lostSelection) {
      setShowPackagingQuantityWarning(true);
      setShowCartItemDetails(true);
    }
    updateQuantity.mutate({
      ...item,
      quantity: quantity_0,
      ...packaging,
      rollDriver: getRollDriver(item)
    }, {
      // Tracked on success only, so a failed update the user retries isn't counted twice
      onSuccess: () => {
        if (lostRollSelection) trackRollQuantityReset("cart");
      },
      onError: () => {
        if (lostRollSelection) clearRollSelectionLost(item.id);
        setShowPackagingQuantityWarning(false);
        setShowCartItemDetails(initialCartDetailsState);
      }
    });
  }
  function handlePackagingQuantityChange(selection: RollDriverSelection) {
    const wasLost = isRollSelectionLost;
    setShowPackagingQuantityWarning(false);
    clearRollSelectionLost(item.id);
    // No Preference on an item already without values and on the same driver needs no update
    const isUnchanged = selection.rollCount === null && item.rollCount === null && item.rollQty === null && getRollDriver(item) === selection.rollDriver;
    if (isUnchanged) return;
    updateQuantity.mutate({
      ...item,
      ...selection
    }, {
      onError: () => {
        if (wasLost) markRollSelectionLost(item.id);
      }
    });
  }
  function handleRemove() {
    const currentButtons = document.querySelectorAll<HTMLButtonElement>("[data-cart-remove-button]");
    const currentIds = Array.from(currentButtons, button => button.dataset.cartRemoveButton ?? "");
    const removedIndex = currentIds.indexOf(item.id);
    deleteItem.mutate(undefined, {
      onSuccess: () => focusAtIndex(removedIndex)
    });
  }
  return <div data-testid="cart-item" className={cn("relative border-b-2 border-gray-100 py-5 text-sm transition-colors duration-700", item.isDuplicate && "bg-[var(--color-mint-green)]")}>
      <div className="flex space-x-5 px-5">
        <div className="flex shrink-0 flex-col items-center">
          <CartItemThumbnail item={item} />
          <ReorderBadge item={item} />
        </div>
        <div className="grow">
          <div className="flex">
            <div className="mr-2 grow">
              <p data-testid="cart-item-name" className="mb-3 font-semibold">
                {item.description || item.marketingName}
              </p>
              {item.width && item.height && <p className="mb-3">
                  {NumberFormatter.formatDecimal(item.width)}&quot; x {NumberFormatter.formatDecimal(item.height)}&quot;
                </p>}
            </div>
            <div className="text-right">
              <CartItemPrice item={item} isLoading={isUpdatingQuantity} />
              <CartItemDiscount item={item} />
            </div>
          </div>
          <CartItemQuantity item={item} customOptionIsSelected={customOptionIsSelected} hasNonPresetQuantity={hasNonPresetQuantity} isSingleQuantity={isSingleQuantity} showQuantityError={updateQuantity.isError} onCustomSelected={() => setCustomOptionIsSelected(true)} onPresetQuantityChange={handleQuantityChange} onCustomQuantityApply={quantity_1 => handleQuantityChange(quantity_1, true)} />
        </div>
      </div>
      <CartItemActions item={item} onRemoveClick={() => setShowRemoveConfirmation(true)} isUpdatingQuantity={isUpdatingQuantity} />
      <CartItemError message={deleteItem.isError ? REMOVE_ERROR_MESSAGE : undefined} />
      <MaterialWarning materialSku={item.sku.material} marketingName={item.sku.marketingName} />
      <PackagingQuantityWarning showWarning={showPackagingQuantityWarning || isRollMode && needsRollSelection({
      rollDriver: getRollDriver(item),
      rollCount: item.rollCount
    }, isRollSelectionLost)} mode={packagingSelection?.mode ?? PackagingMode.Roll} />
      {showRemoveConfirmation && <RemoveConfirmation onRemove={handleRemove} onKeep={() => setShowRemoveConfirmation(false)} isPending={deleteItem.isPending} />}
      <CartItemDetails item={item} packagingSelection={packagingSelection} showPackagingQuantityError={updateQuantity.isError} onPackagingQuantityChange={handlePackagingQuantityChange} isLoading={updateQuantity.isPending} isOpen={showCartItemDetails} setIsOpen={setShowCartItemDetails} />
    </div>;
}